// src/pages/ExpenseEntry.jsx
// Daily expense entry for hospital admin/billing staff
// Two modes: Upload the template Excel OR fill the grid manually on screen
// Cash tally at the bottom — blocks submission if variance > ₹100

import { useState, useEffect, useMemo } from "react";
import { api } from "../services/api";
import { AppDatePicker, AppSelect } from "../components/shared/FormControls";

const TYPE_OPTIONS    = ["Operating", "Capital", "Statutory", "Payroll", "Contra"];

export default function ExpenseEntry({ entity }) {
  const [mode,       setMode]       = useState("grid");   // "grid" | "upload"
  const [date,       setDate]       = useState(today());
  const [rows,       setRows]       = useState([]);
  const [openCash,   setOpenCash]   = useState("");
  const [cashColl,   setCashColl]   = useState("");
  const [closeCash,  setCloseCash]  = useState("");
  const [file,       setFile]       = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState("");
  const [downloading,setDownloading]= useState(false);

  const expenseHeads = entity?.expenseHeads || [];

  // ── Initialise grid rows from entity's expense heads ─────────────────────
  useEffect(() => {
    if (expenseHeads.length > 0 && rows.length === 0) {
      // Show operating expense heads by default, hide others unless user adds
      const defaultHeads = expenseHeads
        .filter(h => h.isActive && h.category === "operating")
        .slice(0, 15)
        .map(h => ({ head: h.name, cash: "", bank: "", paidBy: "Cash", type: "Operating", vendor: "" }));
      setRows(defaultHeads);
    }
  }, [expenseHeads]);

  // ── Totals ────────────────────────────────────────────────────────────────
  const { totalCash, totalBank } = useMemo(() => {
    let tc = 0, tb = 0;
    rows.forEach(r => {
      const cash = parseFloat(r.cash || 0) || 0;
      const bank = parseFloat(r.bank || 0) || 0;
      tc += cash; tb += bank;
    });
    return { totalCash: tc, totalBank: tb };
  }, [rows]);

  const expectedClose = useMemo(() => {
    const o = parseFloat(openCash  || 0) || 0;
    const c = parseFloat(cashColl  || 0) || 0;
    return o + c - totalCash;
  }, [openCash, cashColl, totalCash]);

  const variance = useMemo(() => {
    const ph = parseFloat(closeCash || 0) || 0;
    return ph - expectedClose;
  }, [closeCash, expectedClose]);

  const varStatus = Math.abs(variance) < 0.01 ? "ok"
    : Math.abs(variance) <= 100 ? "warn" : "block";

  // ── Grid row operations ───────────────────────────────────────────────────
  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function addRow() {
    setRows(prev => [...prev, { head: "", cash: "", bank: "", paidBy: "Cash", type: "Operating", vendor: "" }]);
  }

  function removeRow(idx) {
    setRows(prev => prev.filter((_, i) => i !== idx));
  }

  // ── Download expense template ─────────────────────────────────────────────
  async function downloadTemplate() {
    setDownloading(true);
    try {
      await api.download(`/entities/${entity._id}/expense-template`, null, `ExpenseTemplate_${entity.shortCode}.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  }

  // ── Submit grid ───────────────────────────────────────────────────────────
  async function submitGrid() {
    if (varStatus === "block") {
      setError(`Cash variance of ₹${Math.abs(variance).toLocaleString("en-IN")} is too large. Check your entries and closing cash count.`);
      return;
    }

    // Build expense rows from grid
    const expenseRows = [];
    rows.forEach(r => {
      if (parseFloat(r.cash || 0) > 0) {
        expenseRows.push({ date, expenseName: r.head, paidBy: "cash", amount: parseFloat(r.cash), vendor: r.vendor, type: r.type.toLowerCase() });
      }
      if (parseFloat(r.bank || 0) > 0) {
        expenseRows.push({ date, expenseName: r.head, paidBy: "bank_hdfc", amount: parseFloat(r.bank), vendor: r.vendor, type: r.type.toLowerCase() });
      }
    });

    if (expenseRows.length === 0) {
      setError("No amounts entered. Fill in at least one expense before submitting.");
      return;
    }

    setSubmitting(true); setError("");
    try {
      // Submit cash tally alongside expenses
      const [expRes] = await Promise.all([
        api.post(`/entities/${entity._id}/expenses/grid`, {
          date, expenseRows,
          cashTally: {
            openingCash:    parseFloat(openCash  || 0),
            cashCollection: parseFloat(cashColl  || 0),
            cashExpenses:   totalCash,
            expectedClosing: expectedClose,
            physicalClosing: parseFloat(closeCash || 0),
            variance,
            varianceNote: varStatus === "warn" ? "Minor variance — accepted" : "",
          }
        }),
      ]);
      setResult(expRes);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Submit file upload ────────────────────────────────────────────────────
  async function submitFile() {
    if (!file) { setError("Select a file first."); return; }
    setSubmitting(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.upload(`/entities/${entity._id}/expenses/upload`, fd);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Result screen ─────────────────────────────────────────────────────────
  if (result) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, textAlign: "center", padding: 36 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h2 style={{ color: "#1A7A4A", margin: "0 0 8px" }}>Expenses submitted</h2>
          <p style={{ color: "#607D8B", margin: "0 0 24px" }}>
            {result.entriesGenerated} journal {result.entriesGenerated === 1 ? "entry" : "entries"} created
            {result.pendingApprovals > 0 && ` · ${result.pendingApprovals} capital items need administrator approval`}
          </p>
          {result.pendingApprovals > 0 && (
            <div style={S.warnBox}>
              <strong>Capital items detected.</strong> Your administrator will classify these before they are posted.
            </div>
          )}
          <button onClick={() => { setResult(null); setRows(rows.map(r => ({ ...r, cash: "", bank: "" }))); setOpenCash(""); setCashColl(""); setCloseCash(""); }} style={S.btn}>
            Enter tomorrow's expenses
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={S.page}>

      <div style={S.pageHeader}>
        <h1 style={S.h1}>Daily expenses</h1>
        <p style={S.subtitle}>Enter today's expenses or upload the filled template.</p>
      </div>

      {/* Entity + Date */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
        <div style={S.entityBadge}><span style={S.dot} />{entity?.name}</div>
        <AppDatePicker value={date} onChange={setDate} maxDate={new Date()} style={{ width: 150 }} />
      </div>

      {/* Mode toggle */}
      <div style={S.modeToggle}>
        <button onClick={() => setMode("grid")}   style={{ ...S.modeBtn, ...(mode === "grid"   ? S.modeBtnActive : {}) }}>Fill grid</button>
        <button onClick={() => setMode("upload")} style={{ ...S.modeBtn, ...(mode === "upload" ? S.modeBtnActive : {}) }}>Upload template</button>
      </div>

      {/* ── GRID MODE ── */}
      {mode === "grid" && (
        <>
          <div style={S.card}>
            {/* Table header */}
            <div style={S.gridHeader}>
              <div style={{ flex: 3 }}>Expense head</div>
              <div style={{ flex: 1, textAlign: "right" }}>Cash (₹)</div>
              <div style={{ flex: 1, textAlign: "right" }}>Bank (₹)</div>
              <div style={{ flex: 2 }}>Vendor / note</div>
              <div style={{ flex: 1 }}>Type</div>
              <div style={{ width: 28 }} />
            </div>

            {/* Rows */}
            {rows.map((row, idx) => (
              <div key={idx} style={{ ...S.gridRow, background: idx % 2 === 0 ? "#fafbfc" : "#fff" }}>
                {/* Head selector */}
                <div style={{ flex: 3 }}>
                  <AppSelect
                    value={row.head}
                    onChange={value => updateRow(idx, "head", value)}
                    placeholder="Select"
                    isClearable
                    options={expenseHeads.filter(h => h.isActive).map(h => ({ value: h.name, label: h.name }))}
                  />
                </div>

                {/* Cash */}
                <div style={{ flex: 1 }}>
                  <input
                    type="number" min="0" step="1"
                    value={row.cash} placeholder="—"
                    onChange={e => updateRow(idx, "cash", e.target.value)}
                    style={{ ...S.gridInput, textAlign: "right" }}
                  />
                </div>

                {/* Bank */}
                <div style={{ flex: 1 }}>
                  <input
                    type="number" min="0" step="1"
                    value={row.bank} placeholder="—"
                    onChange={e => updateRow(idx, "bank", e.target.value)}
                    style={{ ...S.gridInput, textAlign: "right" }}
                  />
                </div>

                {/* Vendor */}
                <div style={{ flex: 2 }}>
                  <input
                    type="text" value={row.vendor} placeholder="Vendor name (optional)"
                    onChange={e => updateRow(idx, "vendor", e.target.value)}
                    style={S.gridInput}
                  />
                </div>

                {/* Type */}
                <div style={{ flex: 1 }}>
                  <AppSelect
                    value={row.type}
                    onChange={value => updateRow(idx, "type", value)}
                    options={TYPE_OPTIONS.map(t => ({ value: t, label: t }))}
                  />
                </div>

                {/* Remove */}
                <button onClick={() => removeRow(idx)} style={S.removeBtn}>×</button>
              </div>
            ))}

            {/* Totals row */}
            <div style={{ ...S.gridRow, background: "#EBF5FB", borderTop: "2px solid #B0C4D8" }}>
              <div style={{ flex: 3, fontWeight: 600, color: "#1B3F6E", fontSize: 13 }}>Total</div>
              <div style={{ flex: 1, fontWeight: 700, color: "#1B3F6E", textAlign: "right", fontSize: 14 }}>
                {totalCash > 0 ? "₹" + totalCash.toLocaleString("en-IN") : "—"}
              </div>
              <div style={{ flex: 1, fontWeight: 700, color: "#1B3F6E", textAlign: "right", fontSize: 14 }}>
                {totalBank > 0 ? "₹" + totalBank.toLocaleString("en-IN") : "—"}
              </div>
              <div style={{ flex: 2 }} />
              <div style={{ flex: 1 }} />
              <div style={{ width: 28 }} />
            </div>

            {/* Add row */}
            <button onClick={addRow} style={S.addRowBtn}>+ Add expense head</button>
          </div>

          {/* ── CASH TALLY ── */}
          <div style={S.card}>
            <div style={{ fontWeight: 600, color: "#1B3F6E", fontSize: 14, marginBottom: 16 }}>
              Cash tally — {date}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <TallyInput label="Opening cash (₹)" value={openCash}  onChange={setOpenCash}  hint="Cash on hand at start of day" />
              <TallyInput label="Cash collected (₹)" value={cashColl} onChange={setCashColl}  hint="Cash from billing file" />
              <TallyInput label="Closing cash — physical count (₹)" value={closeCash} onChange={setCloseCash} hint="Count cash in hand right now" />
            </div>

            {/* Tally result */}
            {(openCash || cashColl || closeCash) && (
              <div style={{
                ...S.tallyResult,
                background: varStatus === "ok" ? "#E8F5EE" : varStatus === "warn" ? "#FFF8E1" : "#FFEBEE",
                border: `1px solid ${varStatus === "ok" ? "#A5D6A7" : varStatus === "warn" ? "#FFE082" : "#FFCDD2"}`,
                color: varStatus === "ok" ? "#1A7A4A" : varStatus === "warn" ? "#C85A00" : "#B71C1C",
              }}>
                {varStatus === "ok" && <>✓ Cash tallies — no variance</>}
                {varStatus === "warn" && <>⚠ Minor variance of ₹{Math.abs(variance).toLocaleString("en-IN")} — will be noted and accepted</>}
                {varStatus === "block" && <>✗ Variance of ₹{Math.abs(variance).toLocaleString("en-IN")} is too large — recount cash or check entries before submitting</>}
                <div style={{ fontSize: 12, marginTop: 6, opacity: .85 }}>
                  Expected closing: ₹{expectedClose.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            onClick={submitGrid}
            disabled={submitting || varStatus === "block"}
            style={{
              ...S.btn,
              background: submitting || varStatus === "block" ? "#90A4AE" : "#1B3F6E",
              cursor: submitting || varStatus === "block" ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Submitting…" : "Submit expenses for today"}
          </button>
        </>
      )}

      {/* ── UPLOAD MODE ── */}
      {mode === "upload" && (
        <div style={S.card}>
          <p style={{ fontSize: 13, color: "#607D8B", margin: "0 0 16px" }}>
            Download the template, fill it in Excel, and upload. The system maps each row automatically.
          </p>

          <button onClick={downloadTemplate} disabled={downloading} style={{ ...S.outlineBtn, marginBottom: 20 }}>
            {downloading ? "Downloading…" : "⬇ Download expense template"}
          </button>

          <div style={{ borderTop: "1px dashed #CFD8DC", paddingTop: 20 }}>
            <label style={S.label}>Upload completed template</label>
            <input
              type="file" accept=".xlsx,.xls"
              onChange={e => setFile(e.target.files[0])}
              style={{ display: "block", marginBottom: 12, fontSize: 13 }}
            />
            {file && <div style={{ fontSize: 13, color: "#1B3F6E", marginBottom: 12 }}>Selected: {file.name}</div>}
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            onClick={submitFile}
            disabled={submitting || !file}
            style={{
              ...S.btn,
              background: submitting || !file ? "#90A4AE" : "#1B3F6E",
              cursor: submitting || !file ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Processing…" : "Upload and process"}
          </button>
        </div>
      )}
    </div>
  );
}

function TallyInput({ label, value, onChange, hint }) {
  return (
    <div>
      <label style={{ ...S.label, marginBottom: 4 }}>{label}</label>
      <input
        type="number" min="0" step="1" value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="0"
        style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8,
          fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", textAlign: "right" }}
      />
      {hint && <div style={{ fontSize: 11, color: "#90A4AE", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function today() {
  return new Date().toISOString().split("T")[0];
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page:         { maxWidth: 900, margin: "0 auto" },
  pageHeader:   { marginBottom: 20 },
  h1:           { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 6px" },
  subtitle:     { fontSize: 14, color: "#607D8B", margin: 0 },
  entityBadge:  { display: "inline-flex", alignItems: "center", gap: 6, background: "#E3F2FD",
                  color: "#1B3F6E", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 20 },
  dot:          { width: 8, height: 8, borderRadius: "50%", background: "#2E86AB" },
  modeToggle:   { display: "inline-flex", background: "#E3F2FD", borderRadius: 8, padding: 4, marginBottom: 16 },
  modeBtn:      { padding: "7px 20px", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer",
                  background: "transparent", color: "#1B3F6E", fontWeight: 500 },
  modeBtnActive:{ background: "#1B3F6E", color: "#fff" },
  card:         { background: "#fff", borderRadius: 12, padding: "0 0 4px", marginBottom: 16,
                  boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" },
  gridHeader:   { display: "flex", gap: 8, padding: "10px 12px", background: "#1B3F6E",
                  color: "#fff", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" },
  gridRow:      { display: "flex", gap: 8, padding: "6px 12px", alignItems: "center", borderBottom: "1px solid #F0F4F8" },
  gridSelect:   { width: "100%", padding: "6px 8px", border: "1px solid #CFD8DC", borderRadius: 6,
                  fontSize: 13, fontFamily: "inherit", background: "#fff" },
  gridInput:    { width: "100%", padding: "6px 8px", border: "1px solid #CFD8DC", borderRadius: 6,
                  fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },
  removeBtn:    { background: "none", border: "none", color: "#B0BEC5", fontSize: 18, cursor: "pointer",
                  width: 28, flexShrink: 0, padding: 0, lineHeight: 1 },
  addRowBtn:    { display: "block", width: "100%", padding: "10px", background: "#f8fafc",
                  border: "none", color: "#2E86AB", fontSize: 13, cursor: "pointer",
                  fontWeight: 600, borderTop: "1px dashed #CFD8DC" },
  tallyResult:  { borderRadius: 8, padding: "12px 16px", fontSize: 13, fontWeight: 600 },
  btn:          { width: "100%", padding: 14, background: "#1B3F6E", color: "#fff", border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 600, letterSpacing: ".02em" },
  outlineBtn:   { padding: "10px 20px", background: "#fff", border: "2px solid #1B3F6E",
                  color: "#1B3F6E", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  label:        { display: "block", fontSize: 11, fontWeight: 600, color: "#455A64",
                  textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 },
  errorBox:     { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8,
                  padding: "12px 16px", color: "#B71C1C", fontSize: 13, marginBottom: 12 },
  warnBox:      { background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 8,
                  padding: "12px 16px", color: "#C85A00", fontSize: 13, marginBottom: 16 },
};
